import * as vue from 'vue'
import { mapState } from 'pinia'

import EventBus from '../event-bus/EventBus'
import { useAdaStore } from '../stores/ada'
import AdaSlotMarker from '../components/ad-slots/AdaSlotMarker.vue'

export default {
  data () {
    return {
      markers: []
    }
  },
  computed: {
    ...mapState(useAdaStore, ['blockAds', 'isAsyncContentMode'])
  },
  beforeMount () {
    EventBus.$on('ad-loader:enable-async-mode', this.asyncMode = true)
  },
  mounted () {
    if (!this.blockAds && !this.isAsyncContentMode) {
      this.checkParagraphTypeAndInjectMarkers()
    }
  },
  methods: {
    checkParagraphTypeAndInjectMarkers () {
      if (this.$options.name === 'ParagraphTypeHtml') {
        this.injectMarkerToHtmlParagraph()
      } else if (this.$options.name === 'TeaserGroup') {
        this.injectMarkerToTeaserGroup()
      } else if (this.$options.name === 'ParagraphTypeEpgSearch' && this.isAsyncContentMode) {
        this.injectMarkerToEpgSearch()
      } else if (this.$options.name === 'ParagraphTypeVodSearch' && this.isAsyncContentMode) {
        this.injectMarkerToTeaserGroup(true)
      } else if (this.$options.name === 'ParagraphTypeSearch' && this.isAsyncContentMode) {
        this.injectMarkerToTeaserGroup(true)
      }
    },
    createAdaSlotMarker (child, index, hasAsyncContent = false) {
      let marker = null
      let app = null

      const appConfig = {
        render: () => vue.h(AdaSlotMarker, {
          aboveParagraph: {
            index: this.$options.name + '-' + index,
            type: this.$options.name,
            nested: true,
            hasAsyncContent,
            wordcount: child.innerText.split(' ').length
          },
          onRemove: () => {
            this.removeAdaSlotMarker({ marker, app })
          }
        })
      }

      // some optimization to ensure that we completely remove the marker from the DOM and Vue instance - if we want to remove it
      const el = document.createElement('div')
      app = vue.createApp(appConfig)
      marker = app.mount(el)

      this.markers.push(marker)
      return marker
    },
    removeAdaSlotMarker ({ marker, app }) {
      const index = this.markers.indexOf(marker)
      if (index > -1) {
        this.markers.splice(index, 1)
        app?.unmount() // remove Vue instance
        marker?.remove?.() // remove DOM element
      }
    },
    injectMarkerToHtmlParagraph () {
      const EXCLUDE_POSITION_AFTER_TAGS = ['H1', 'H2', 'H3']
      this.$el.querySelectorAll(':scope > *').forEach((child, index) => {
        if (child.nextSibling && !EXCLUDE_POSITION_AFTER_TAGS.includes(child.tagName)) {
          const marker = this.createAdaSlotMarker(child, index)
          this.$el.insertBefore(marker.$el, child.nextSibling)
        }
      })
    },
    injectMarkerToTeaserGroup (isAsyncContent = false) {
      // restrict to certain teaser group types - for AW we need this injection mostly for zodiac teaser
      // otherwise we also hijack teaser groups on overview pages where we don't want them
      const allowedTeaserGroupTypes = ['bx-teaser-container--info', 'bx-teaser-container--recipe', 'bx-teaser-container--lexicon', 'bx-teaser-container--vod']
      if (isAsyncContent) {
        // ParagraphTypeSearch TeaserGoup - but only addin this selector, while we are in async content mode for this paragraph type
        allowedTeaserGroupTypes.push('bx-teaser-container--one-third-width')
      }

      if (allowedTeaserGroupTypes.includes(this.teaserGroupType)) {
        this.injectMarkerToContainer(
          '.bx-teaser-container:not(.bx-js-prevent-slot-marker):not(.bx-teaser-container--dialog) .bx-teaser-container__wrapper',
          `.bx-teaser-container:not(.bx-js-prevent-slot-marker):not(.bx-teaser-container--dialog) .bx-teaser:nth-child(${this.getTeaserPerRow()}n)`,
          isAsyncContent
        )
      }
    },
    getTeaserPerRow () {
      let counted = 0
      let referenceTop = null
      const teaserList = Array.from(this.$el.querySelectorAll('.bx-teaser'))
      if (teaserList?.length > 0) {
        // we only need to itterate through the teasers until we find a new row
        // to prevent unnecessary loops, we use for instead of forEach
        for (let i = 0; i < teaserList.length; i++) {
          const teaserTop = teaserList[i].getBoundingClientRect().top
          if (!referenceTop || teaserTop === referenceTop) {
            counted++
            referenceTop = teaserTop
          } else {
            break
          }
        }
      }
      return counted
    },
    injectMarkerToEpgSearch () {
      this.injectMarkerToContainer(
        '.bx-epg-search__broadcasts',
        '.bx-epg-search__broadcasts .bx-epg-search__row',
        true // hasAsyncContent is true for EPG search as it loads content asynchronously
      )
    },
    injectMarkerToContainer (wrapperSelector, childSelector, hasAsyncContent = false) {
      const teaserWrapper = this.$el.querySelector(wrapperSelector)
      this.$el.querySelectorAll(childSelector).forEach((child, index) => {
        if (child.nextSibling) {
          const marker = this.createAdaSlotMarker(child, index, hasAsyncContent)
          teaserWrapper.insertBefore(marker.$el, child.nextSibling)
        }
      })
    },
    /**
     * Injects the ad slot marker and allocates ad slots in dynamic content.
     * This method is called when dynamic content is loaded and ad slots need to be allocated.
     * It sets the async content refresh flag to true, flushes the ad slot marker,
     * and flushes the activated ad slot marker.
     * After the next tick, it sets the async content refresh flag to false,
     * calls the provided callback if any, and emits an event to notify that ad slots allocation can proceed.
     * @param {Function} [callback=false] - Optional callback function to be called after the ad slots are allocated.
     */
    injectMarkerAndAllocateAdSlotsInDynamicContent (callback = false) {
      if (this.dynamicAdSlotsAllocated) {
        this.setAsyncContentRefresh(true)
        this.flushAdSlotMarker()
        this.flushActivatedAdSlotMarker()
      }

      this.$nextTick(() => {
        this.setAsyncContentRefresh(false)
        if (callback) {
          callback()
        }
        EventBus.$emit('ad-loader:allocation')
      })
    }
  }
}
